import { useTranslate } from "ra-core";

import { APP_LOCALE } from "../misc/locale";
import type { QuotePlanParameters } from "../types";
import {
  PaymentPlanError,
  calculatePaymentSchedule,
  type ScheduleRow,
} from "./paymentSchedule";

const moneyFormat = new Intl.NumberFormat(APP_LOCALE, {
  style: "currency",
  currency: "MXN",
});

/** Formats an amount in MXN (es-MX). */
export const formatMxn = (amount: number): string => moneyFormat.format(amount);

/** Safe wrapper: null instead of throwing while a plan or price is invalid. */
export const tryCalculateSchedule = (
  price: number,
  plan: QuotePlanParameters,
): ScheduleRow[] | null => {
  try {
    return calculatePaymentSchedule(price, plan);
  } catch (error) {
    if (error instanceof PaymentPlanError) return null;
    throw error;
  }
};

/**
 * Payment schedule table shared by the template preview and the quote panel
 * (one calculator, one rendering). Every amount uses tabular numbers.
 */
export const PlanSchedule = ({
  rows,
  showPercentage = true,
  large = false,
}: {
  rows: ScheduleRow[];
  /** The public client page hides the per-payment percentage column. */
  showPercentage?: boolean;
  /** Body-size text (the public page needs at least 15 px). */
  large?: boolean;
}) => {
  const translate = useTranslate();
  const when = (row: ScheduleRow) => {
    if (row.monthOffset === null)
      return translate("crm.quoter.schedule.at_end");
    return row.monthOffset === 0
      ? translate("crm.quoter.schedule.at_signing")
      : translate("crm.quoter.schedule.month", { month: row.monthOffset });
  };
  return (
    <div className="overflow-x-auto">
      <table
        className={`w-full tabular-nums ${large ? "text-base" : "text-sm"}`}
      >
        <thead>
          <tr
            className={`text-left text-muted-foreground ${large ? "text-sm" : "text-xs"}`}
          >
            <th className="py-1 pr-2 font-medium">
              {translate("crm.quoter.schedule.concept")}
            </th>
            <th className="py-1 pr-2 font-medium">
              {translate("crm.quoter.schedule.when")}
            </th>
            {showPercentage ? (
              <th className="py-1 pr-2 text-right font-medium">%</th>
            ) : null}
            <th className="py-1 text-right font-medium">
              {translate("crm.quoter.schedule.amount")}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={`${row.concept}-${row.number}`} className="border-t">
              <td className="py-1 pr-2">
                {translate(`crm.quoter.concepts.${row.concept}`)} {row.number}
              </td>
              <td className="py-1 pr-2">{when(row)}</td>
              {showPercentage ? (
                <td className="py-1 pr-2 text-right">{row.percentage}</td>
              ) : null}
              <td className="py-1 text-right">{formatMxn(row.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
